import { create } from 'zustand';
import type { Message } from '@/types/domain';

interface MessagesState {
  byChat: Record<number, Message[]>;
  setMessages: (chatId: number, messages: Message[]) => void;
  prependMessages: (chatId: number, messages: Message[]) => void;
  appendMessage: (chatId: number, message: Message) => void;
  /**
   * F-180 A9 — mutate an optimistic message in place by `client_nonce`.
   * Used by the composer to flip `status: 'pending'` → `'failed'` when
   * a WS / REST send throws, or back to `'pending'` on retry.
   */
  updateByNonce: (
    chatId: number,
    nonce: string,
    patch: Partial<Message>,
  ) => void;
  removeByNonce: (chatId: number, nonce: string) => void;
}

export const useMessagesStore = create<MessagesState>((set) => ({
  byChat: {},
  setMessages: (chatId, messages) =>
    set((s) => ({ byChat: { ...s.byChat, [chatId]: messages } })),
  prependMessages: (chatId, messages) =>
    set((s) => ({
      byChat: { ...s.byChat, [chatId]: [...messages, ...(s.byChat[chatId] ?? [])] },
    })),
  appendMessage: (chatId, message) =>
    set((s) => {
      const existing = s.byChat[chatId] ?? [];
      // dedupe by id or client_nonce
      const filtered = existing.filter(
        (m) =>
          m.id !== message.id &&
          (!message.client_nonce || m.client_nonce !== message.client_nonce),
      );
      return { byChat: { ...s.byChat, [chatId]: [...filtered, message] } };
    }),
  updateByNonce: (chatId, nonce, patch) =>
    set((s) => {
      const list = s.byChat[chatId] ?? [];
      const next = list.map((m) =>
        m.client_nonce === nonce ? { ...m, ...patch } : m,
      );
      return { byChat: { ...s.byChat, [chatId]: next } };
    }),
  removeByNonce: (chatId, nonce) =>
    set((s) => {
      const list = s.byChat[chatId] ?? [];
      return {
        byChat: {
          ...s.byChat,
          [chatId]: list.filter((m) => m.client_nonce !== nonce),
        },
      };
    }),
}));
